import { httpClient } from '../funcs'
import { notifyAboutError, notifyUser } from './notifyUser'
import {
    loadToken,
    getToken,
    removeToken,
    persistTokensFromUserSelfResponse,
    isOAuth2User,
    ensureValidToken,
    isRefreshInFlight,
} from './saveToken'
import { isRefreshTokenExpiringSoon, getOAuth2TokensFromStorage, isTokenExpired } from './oauth2Tokens'
import {
    isAccessTokenExpiredErrorText,
    trackAuthSessionEvent,
    SessionClearReason,
} from './authSessionTelemetry'

/**
 * Загружает информацию о пользователе и обрабатывает все сценарии с токенами
 */
export async function loadSelf(props: any): Promise<void> {
    loadToken()
    if (isOAuth2User() && (!getToken() || isTokenExpired())) {
        const restored = await ensureValidToken(props)
        if (!restored && !getToken()) {
            handleTokenRefreshFailure(props, 'loadSelf_refresh_fail')
            return
        }
    }

    try {
        // Запрашиваем информацию о пользователе
        const { data: user } = await httpClient.get('/user/self')
        
        persistTokensFromUserSelfResponse(user)
        
        // Проверяем статус refresh token для предупреждений
        await checkRefreshTokenStatus(user, props)
        
        // Загружаем баланс
        const { data: balance } = await httpClient.get('/get-balance')
        
        // Сохраняем данные в store
        props?.$commitToStore({
            userSession: user,
            balance: balance.balance
        })
        
    } catch (error: any) {
        await handleLoadSelfError(error, props)
    }
}

/**
 * Обрабатывает ошибки при загрузке пользователя
 */
async function handleLoadSelfError(error: any, props: any): Promise<void> {
    if (error?.response?.status !== 401) {
        notifyAboutError(props, error)
        return
    }

    const errorText = error?.response?.data || error?.message || ''
    const expiredAccess = isAccessTokenExpiredErrorText(errorText)

    // Пока идёт общий refresh — не сносим сессию из-за параллельного 401
    if (isRefreshInFlight()) {
        trackAuthSessionEvent('loadSelf_401_while_refresh', {
            expired_access: expiredAccess,
            url: '/user/self',
        })
        try {
            const isTokenValid = await ensureValidToken(props)
            if (isTokenValid) {
                await retryLoadSelf(props)
                return
            }
        } catch (retryError) {
            notifyAboutError(props, retryError)
            return
        }
        handleTokenRefreshFailure(props, 'loadSelf_refresh_fail')
        return
    }

    if (expiredAccess && isOAuth2User()) {
        console.log('OAuth2 token expired in loadSelf, attempting to refresh...')
        const isTokenValid = await ensureValidToken(props)
        if (isTokenValid) {
            try {
                await retryLoadSelf(props)
                return
            } catch (retryError) {
                console.log('Retry failed after token refresh:', retryError)
                if ((retryError as any)?.response?.status === 401) {
                    handleTokenRefreshFailure(props, 'loadSelf_retry_401')
                } else {
                    notifyAboutError(props, retryError)
                }
                return
            }
        }
        handleTokenRefreshFailure(props, 'loadSelf_refresh_fail')
        return
    }

    if (expiredAccess) {
        console.log('No refresh token available, logging out immediately')
        handleTokenRefreshFailure(props, 'loadSelf_refresh_fail')
        return
    }

    // Другой 401 (не «токен истек») — не считаем это истечением сессии
    trackAuthSessionEvent('loadSelf_other_401', {
        body: String(errorText).slice(0, 200),
    })
    notifyAboutError(props, error)
}

/**
 * Повторяет загрузку пользователя после обновления токена
 */
async function retryLoadSelf(props: any): Promise<void> {
    const { data: user } = await httpClient.get('/user/self')
    
    persistTokensFromUserSelfResponse(user)
    
    const { data: balance } = await httpClient.get('/get-balance')
    
    props?.$commitToStore({
        userSession: user,
        balance: balance.balance
    })
}

/**
 * Выход из системы
 */
export function signOut(props: any): void {
    props.$commitToStore({
        userSession: null,
        balance: null,
        signedOut: true
    })
    localStorage.removeItem("registerForm")
    removeToken('explicit_signout')
}

/**
 * Обрабатывает неудачное обновление токена
 */
export function handleTokenRefreshFailure(
    props: any,
    reason: SessionClearReason = 'unknown',
): void {
    notifyUser(props, "Сессия истекла", "Необходимо авторизоваться по номеру телефона заново")

    props?.$commitToStore({
        userSession: null,
        balance: null,
        signedOut: true
    })
    localStorage.removeItem("registerForm")
    removeToken(reason)
}

/**
 * Проверяет статус refresh token и показывает предупреждения
 */
async function checkRefreshTokenStatus(user: any, props: any): Promise<void> {
    // Проверяем только для OAuth2 пользователей
    if (!isOAuth2User()) {
        return
    }
    
    // Получаем OAuth2 токены из localStorage
    const oauth2Tokens = getOAuth2TokensFromStorage()
    if (oauth2Tokens && oauth2Tokens.refresh_token) {
        const isExpiringSoon = isRefreshTokenExpiringSoon(oauth2Tokens.refresh_token, 7)
        if (isExpiringSoon) {
            console.warn('Refresh token истекает в ближайшие дни')
            // Можно показать уведомление пользователю
            // notifyUser(props, 'Ваша сессия скоро истечет. Пожалуйста, сохраните важные данные.')
        }
    }
}